import React, { useEffect, useState } from "react";
import { useMutation } from "@apollo/client";
import styles from "./index.module.css";
import {
  CircularProgress,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  Typography,
  Tooltip,
  IconButton,
  TextField,
  Container
} from "@material-ui/core";
import DoneIcon from '@material-ui/icons/Done';
import ClearIcon from '@material-ui/icons/Clear';
import EditIcon from '@material-ui/icons/Edit';
import Grid from "@material-ui/core/Grid";
import {
  IBooking,
  IBookingVehicleGroup,
  IVehiclePriceGroup,
  IAddonRequirement,
  ILocationSurchargeRules,
  PriceRuleOperator,
  ValueType,
  BILLING_CYCLE_NAME,
  BookingStatus,
  IBookingProtectionPlan,
} from "../../../../reducers/bookings/types";
import { StyledTableCell, StyledTableRow } from "../../../StyledTable";
import {
  DATE_TYPE,
  capitalize,
  formatGraphQLErrorMessage,
  getRetnalTaxAmount,
  getTaxDescription,
  getVehicleGroupApplicableTaxesDetails,
  isLongTermBooking,
  rateTypeName,
  toCurrency,
} from "../../../utils";
import { DateTime as d, Settings } from "luxon";
import { useSelector } from "react-redux";
import { IAppState } from "../../../../store";
import { RATE_TYPES } from "../../utils";
import dayjs from "dayjs";
import { Storage } from "aws-amplify";
import { DepositCollection } from "../../../consts";
import { getLocalizedBookingSyntex, getLocalizedDateFormat, getLocalizedTaxSyntex } from "../../../../utils/localized.syntex";
import { useSnackBar } from "../../../SnackBarContext/SnackBarContext";
import { SnackBarVariant } from "../../../SnackbarWrapper/SnackbarWrapper";
import { ApolloError } from "apollo-boost";
import { ADD_BOOKING_PO_NUMBER } from "../../../../graphql/bookings/addBookingPoNumberMutation";
import SubscriptionPaymentView from "../../NewBooking/Quote/SubscriptionPaymentView";
import DiscountOffersSection from "../../NewBooking/Quote/DiscountOffersSection";
import { IBranch } from "../../../../reducers/website/types";
import { getSignedUrl } from "../../../../utils/getSignedUrl";
import { useTranslation } from "react-i18next";
import { UserRole } from "../../../../reducers/auth/types";
export interface IProps {
  booking: IBooking;
  onBookingUpdated?: (updated: Partial<IBooking>) => void;
}

const Quote: React.FC<IProps> = (props) => {
  const snackbar = useSnackBar();
  const [booking, setBooking] = useState<IBooking>();
  const [weeklyTaxAmount, setWeekyTaxAmount] = useState<number>(0);
  const [bookingDurationInDays, setBookingDurationInDays] = useState<number>(0);
  const [isProrated, setIsProrated] = useState<boolean>(false);
  const [proratedFraction, setProratedFraction] = useState<number>(1);
  const [fixedRateAddonAmount, setFixedRateAddonAmount] = useState<number>(0);
  const website = useSelector(
    (state: IAppState) => state.consumerWebsiteReducer.consumerWebsite
  );
  const [collectDeposit, setCollectDeposit] = useState<boolean>(false);
  const branchData = website.branches;
  const { currency, locale } = website.organisation;
  const { country } = website.organisation.address;
  const [logoUrl, setLogoUrl] = useState<any>("");
  const [poNumber, setPoNumber] = useState<string>("");
  const [isEditablePONumber, setIsEditablePONumber] = useState<boolean>(false);
  const [weeklyRentalAmount, setWeeklyRentalAmount] = useState<number>(0);
  const [weeklyAddonAmount, setWeeklyAddonAmount] = useState<number>(0);
  const [weeklyProtectionPlanAmount, setWeeklyProtectionPlanAmount] = useState<number>(0);
  const [weeklyInsuranceAmount, setWeeklyInsuranceAmount] = useState<number>(0);
  const [weeklySubscription, setWeeklySubscription] = useState<boolean>(false);
  const [quoteInfo, setQuoteInfo] = useState<boolean>(false);
  const isContractHire = website.organisation.bookingCategories.some((category) => category.isContractHire === true)
  const { t } = useTranslation();
  const authState = useSelector((state: IAppState) => state.authReducer);
  const { user } = authState

  const [addBookingPoNumber] = useMutation(ADD_BOOKING_PO_NUMBER, {
    onCompleted: (data) => {
      snackbar({
        message: `${getLocalizedBookingSyntex(country)} ${t("root:purchaseOrderUpdated")}`,
        variant: SnackBarVariant.SUCCESS
      })
      if (booking) {
        setBooking({
          ...booking,
          poNumber: data.addBookingPoNumber
        })
      }
      setPoNumber(data.addBookingPoNumber)
    },
    onError: (error: ApolloError) =>
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR
      })
  });

  const addPoNumberToBooking = (_poNumber: string) => {
    if (booking) {
      addBookingPoNumber({
        variables: {
          bookingId: booking.id,
          poNumber: _poNumber
        }
      })
    }
  }

  const getApplicableRentalAmount = (
    baseRate: number,
    operator?: string,
    valueType?: ValueType,
    value?: number,
    riskBasedPricingApplied?: boolean,
    riskBasedPricingAmount?: number
  ) => {
    let applicableRentalAmount = baseRate;
    if (operator && valueType && value) {
      switch (operator) {
        case PriceRuleOperator.SURGE:
          if (valueType === ValueType.VARIABLE) {
            applicableRentalAmount = baseRate + Math.round((baseRate * value) / 100);
          } else {
            applicableRentalAmount = baseRate + value;
          }
          break;
        case PriceRuleOperator.DISCOUNT:
          if (valueType === ValueType.VARIABLE) {
            applicableRentalAmount = baseRate - Math.round((baseRate * value) / 100);
          } else {
            applicableRentalAmount = baseRate - value;
          }
          applicableRentalAmount = Math.max(applicableRentalAmount, 0);
          break;
      }
    }
    if (riskBasedPricingApplied) {
      applicableRentalAmount += riskBasedPricingAmount ?? 0;
    }
    return applicableRentalAmount;
  };

  useEffect(() => {
    if (props.booking) {
      const _bookingData: IBooking = props.booking;
      let payableAddonAmount = 0;
      let taxAmount = 0;
      let weeklyRentalAmountRentalAmount = 0;
      let weeklyInsuranceAmount = 0;
      let weeklyAddonAmount = 0
      let protectionPlanAmount = 0;
      if (_bookingData.protectionPlan && !_bookingData.protectionPlan.hasFixedRate) {
        protectionPlanAmount = _bookingData.protectionPlan.rate * (_bookingData.paymentDetails?.rentalTime || 1);
        if (_bookingData.protectionPlan.tax?.value) {
          taxAmount += Math.round(_bookingData.protectionPlan.rate * _bookingData.protectionPlan.tax.value / 100) * (_bookingData.paymentDetails?.rentalTime || 1);
        }
      }

      if (_bookingData.addonRequirements && _bookingData.addonRequirements.length) {
        for (let i = 0; i < _bookingData.addonRequirements.length; i++) {
          const addonR = _bookingData.addonRequirements[i];
          if (!addonR.hasFixedRate) {
            weeklyAddonAmount += addonR.rate * addonR.quantity * _bookingData.paymentDetails.rentalTime
            taxAmount +=
              addonR.tax && addonR.tax.value
                ? Math.round((addonR.rate * addonR.tax.value) / 100) *
                addonR.quantity *
                _bookingData.paymentDetails.rentalTime
                : 0;
          }
        }
      }
      setIsProrated(_bookingData.paymentDetails.isProrated ? true : false)
      let fraction = 1;
      if (_bookingData.paymentDetails.isProrated && _bookingData.paymentDetails.proratedBillingDays && _bookingData.paymentDetails.standardBillingDays) {
        fraction = parseFloat((_bookingData.paymentDetails.proratedBillingDays / _bookingData.paymentDetails.standardBillingDays).toString())
        setProratedFraction(fraction)
      }
      let bookingDuration = 0;
      const longTermBooking = isLongTermBooking(_bookingData);
      if (longTermBooking) {
        switch (_bookingData.rateTypeName) {
          case BILLING_CYCLE_NAME.DAILY:
            if (_bookingData.isRecurringBilling && _bookingData.billingFrequency) {
              bookingDuration = _bookingData.billingFrequency;
              setBookingDurationInDays(bookingDuration);
            }
            break;
          case BILLING_CYCLE_NAME.WEEKLY:
            if (_bookingData.isRecurringBilling && _bookingData.billingFrequency) {
              bookingDuration = 7 * _bookingData.billingFrequency
            } else {
              bookingDuration = 7
            }
            setBookingDurationInDays(bookingDuration);
            break;
          case BILLING_CYCLE_NAME.MONTHLY:
            if (_bookingData.isRecurringBilling && _bookingData.billingFrequency) {
              let idealEndDate = d.fromISO(_bookingData.pickupDateTime || "").plus({ months: _bookingData.billingFrequency }).toUTC().toISO();
              bookingDuration = d.fromISO(idealEndDate).diff(d.fromISO(_bookingData.pickupDateTime), "day").days;
              setBookingDurationInDays(bookingDuration);
            }
            break;
          default:
            break;
        }
      } else {
        bookingDuration = Math.ceil(d.fromISO(_bookingData.dropoffDateTime || "").diff(d.fromISO(_bookingData.pickupDateTime || ""), ["days"]).days);
      }
      if (_bookingData.isSubscription && _bookingData.activeSubscription) {
        const flag = _bookingData.activeSubscription.subscription.rateTypeName === RATE_TYPES.WEEKLY ? true : false
        setWeeklySubscription(flag)
      }
      const selectedBranch = branchData.find(
        (branch) => branch.id === _bookingData.branchId
      );
      if (selectedBranch) {
        Settings.defaultZone = selectedBranch.timeZone || "Europe/London";
      }
      if (_bookingData && _bookingData.vehicleGroups.length) {
        for (let j = 0; j < _bookingData.vehicleGroups.length; j++) {
          const vehicleGroup = _bookingData.vehicleGroups[j]
          let rentalAmount = getApplicableRentalAmount(
            vehicleGroup.baseRate,
            vehicleGroup.priceRuleOperator,
            vehicleGroup.priceRuleValueType,
            vehicleGroup.priceRuleAmount,
            vehicleGroup.riskBasedPricingApplied,
            vehicleGroup.riskBasedPricingAmount
          ) *
            vehicleGroup.count *
            _bookingData.paymentDetails.rentalTime;
          weeklyRentalAmountRentalAmount += rentalAmount
          const applicableTaxes = getVehicleGroupApplicableTaxesDetails(_bookingData.tax || [], typeof vehicleGroup.vehicleGroup === 'string' ? vehicleGroup.vehicleGroup : vehicleGroup.vehicleGroup.id);
          const rentalTaxAmount = getRetnalTaxAmount(
            applicableTaxes,
            rentalAmount,
            bookingDuration,
            vehicleGroup.count
          );
          taxAmount += rentalTaxAmount
        }
      }
      const totalVehicles = _bookingData.vehicleGroups.reduce(
        (accumulator, current) => accumulator + current.count,
        0
      );
      if (_bookingData.insurancePolicy && _bookingData.insuranceRate) {
        weeklyInsuranceAmount = _bookingData?.insuranceRate *
          totalVehicles *
          (_bookingData.paymentDetails?.rentalTime || 1)
      }
      setWeeklyAddonAmount(weeklyAddonAmount)
      setWeeklyProtectionPlanAmount(protectionPlanAmount);
      setWeeklyRentalAmount(weeklyRentalAmountRentalAmount)
      setBookingDurationInDays(bookingDuration);
      setFixedRateAddonAmount(payableAddonAmount);
      setWeekyTaxAmount(taxAmount);
      setWeeklyInsuranceAmount(weeklyInsuranceAmount);
      setBooking(_bookingData);
      setPoNumber(props.booking.poNumber || "")
    }
  }, [props.booking]);

  useEffect(() => {
    if (website.organisation && website.organisation.logoUrl) {
      getLogoUpload(website.organisation.logoUrl)
    }
  }, [website.organisation]);

  useEffect(() => {
    if (website && booking) {
      const selectedbranch = website.branches.find(branch => branch.id === booking.branchId);
      if (selectedbranch?.depositCollection === DepositCollection.AT_BOOKING_CONFIRMATION) {
        setCollectDeposit(true);
      }
    }
  }, [website, booking])

  const branch = branchData.find((branch: IBranch) => branch.id === props.booking?.branchId);

  useEffect(() => {
    if (branch) {
      const enableQuoteInfo = branch?.enableQuoteInfo ? branch?.enableQuoteInfo : false
      if (enableQuoteInfo) {
        setQuoteInfo(true)
      }
    }
  }, []);

  const getLogoUpload = async (logoUrl: string) => {
    const file = await getSignedUrl(logoUrl).then((result) => {
      return result;
    }).catch((err) => {
    });
    const response = await fetch(file as string).then((res) => {
      return res;
    });
    if (!response.ok) {
      setLogoUrl("");
      return;
    }
    setLogoUrl(file);

  };

  if (!booking) {
    return <CircularProgress size={20} thickness={5} />;
  }

  const isBusinessCustomerBooking =
    booking.customerType === "business" &&
      booking.businessCustomer &&
      booking.businessCustomer.id !== ""
      ? true
      : false;

  const getRateTypeString = () => {
    const rateType = booking?.rateTypeName;
    if (rateType) {
      switch (rateType) {
        case "hourly":
          return "Hour (s)";
        case "daily":
          return "Day (s)";
        case "weekly":
          return "Week (s)";
        case "monthly":
          return "Month (s)";
        default:
          return "Day (s)";
      }
    }
  };

  const getAddonRate = (addon: IAddonRequirement, rentalTime: number) => {
    let rate = addon.rate * addon.quantity;
    if (!addon.hasFixedRate && rentalTime) {
      rate = rate * rentalTime * proratedFraction;
    }
    return rate;
  };

  const getProtectionPlanRate = (protectionPlan: IBookingProtectionPlan, rentalTime: number) => {
    let rate = protectionPlan.rate;
    if (!protectionPlan.hasFixedRate && rentalTime) {
      rate = Math.round(rate * proratedFraction) * rentalTime
    }
    return rate;
  }

  const address = `${website.organisation!.address.street}, ${website.organisation!.address.city
    }, ${website.organisation!.address.zipcode}`;

  let COLUMN_NAMES = [
    "ITEM",
    "DESCRIPTION",
    "QUANTITY"
  ];

  if (website.organisation.closeGroupSharingEnabled) {
    COLUMN_NAMES = [...COLUMN_NAMES, "DURATION"];
  } else {
    COLUMN_NAMES = [...COLUMN_NAMES,
      "UNIT PRICE",
      "DURATION",
      "DISCOUNTED PRICE",
      "VAT/TAX",
      "PRICE",
    ];
  }

  return (
    <>
      {
        booking.isSubscription || booking.bookingCategory === "SUBSCRIPTION" ? (
          <Container>
            <SubscriptionPaymentView
              booking={booking}
              onBookingUpdated={(updated) => {
                setBooking({ ...booking, ...updated });
                props.onBookingUpdated?.(updated);
              }}
            />
          </Container>
        ) : (
          <Grid container>
            <Grid item xs={12} style={{ display: "flex" }}>
              <div
                className={styles.card}
                style={{ marginRight: 0, marginBottom: -15 }}
              >
                <div className="flex">
                  <div className={styles.logo}>
                    {logoUrl && <img src={logoUrl || ""} alt="logo" />}
                  </div>
                  <div className="flex fill col-flex cross-end">
                    <Typography variant={"h4"} align="right">
                      {website.organisation.name}
                    </Typography>
                    <Typography variant={"body1"} align="right">
                      {address}
                    </Typography>
                    <Typography variant="body1" align="right">
                      {t("root:telNo")}: {website.organisation.phoneNumber.phone} {"|"} {t("root:email")}:{" "}
                      {website.organisation.emailId}
                    </Typography>
                    <Typography variant="body1" align="right">
                      {t("root:vatNo")}: {website.organisation.orgVatNumber} {"|"} {t("root:companyRegistrationNumber")}:{" "}
                      {website.organisation.orgRegNumber}
                    </Typography>
                  </div>
                </div>
              </div>
            </Grid>
            <Grid item xs={12} md={4} style={{ display: "flex" }}>
              <div className={styles.card}>
                <div className="margin-bottom">
                  <Typography
                    variant="body1"
                    className="opacity60 padding-bottom--small"
                  >
                    {t("root:customer")}:
                  </Typography>
                  <Typography variant="body1" className="semi-bold">
                    {`${isBusinessCustomerBooking
                      ? booking.businessCustomer?.businessName || ""
                      : `${booking.customer?.firstName} ${booking.customer?.lastName}`
                      }`}
                  </Typography>
                </div>
                <div className="margin-bottom">
                  <Typography
                    variant="body1"
                    className="opacity60 padding-bottom--small"
                  >
                    {t("root:emailAddress")}:
                  </Typography>
                  <Typography variant="body1" className="semi-bold">
                    {`${isBusinessCustomerBooking
                      ? booking.businessCustomer?.billing.email
                      : booking.customer
                        ? booking.customer.email
                        : ""
                      }`}
                  </Typography>
                </div>
                <div>
                  <Typography
                    variant="body1"
                    className="opacity60 padding-bottom--small"
                  >
                    {t("root:phoneNumber")}:
                  </Typography>
                  <Typography variant="body1" className="semi-bold">
                    {`${isBusinessCustomerBooking
                      ? booking.businessCustomer?.billing.phoneNumber.phone || ""
                      : booking.customer
                        ? booking.customer.phoneNumber.phone
                        : ""
                      }`}
                  </Typography>
                </div>
              </div>
            </Grid>
            <Grid item xs={12} md={3} style={{ display: "flex" }}>
              <div className={styles.card} style={{ justifyContent: "flex-start" }}>
                <div className="margin-bottom">
                  <Typography
                    variant="body1"
                    className="opacity60 padding-bottom--small"
                  >
                    {`${getLocalizedBookingSyntex(country)} ${t("root:reference")}:`}
                  </Typography>
                  <Typography variant="body1" className="semi-bold">
                    {booking.referenceNumber}
                  </Typography>
                </div>
                <div>
                  <Typography
                    variant="body1"
                    className="opacity60 padding-bottom--small"
                  >
                    {t("root:dateCreated")}:
                  </Typography>
                  <Typography variant="body1" className="semi-bold">
                    {getLocalizedDateFormat(country, booking.dateCreated, DATE_TYPE.EXPANDED)}
                  </Typography>
                </div>
                <div className="margin-top">
                  {isEditablePONumber ? (<Grid item xs={10} container className={"po-number-main"}>
                    <TextField
                      label={t("root:poNumber")}
                      id="po-number"
                      fullWidth
                      value={poNumber}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setPoNumber(e.target.value)
                      }}
                      InputProps={{
                        endAdornment: (
                          <>
                            {
                              <Tooltip title={t("misc:save")} style={{ padding: 0 }}>
                                <IconButton
                                  aria-label="toggle po number "
                                  onClick={() => {
                                    addPoNumberToBooking(poNumber)
                                    setIsEditablePONumber(false)
                                  }}
                                >
                                  <DoneIcon />
                                </IconButton>
                              </Tooltip>
                            }
                            <Tooltip title={t("misc:close")} style={{ padding: 0 }}>
                              <IconButton
                                aria-label="toggle po number"
                                onClick={() => {
                                  setPoNumber(booking?.poNumber || "")
                                  setIsEditablePONumber(false)
                                }}
                              >
                                <ClearIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )
                      }}
                    />
                  </Grid>) : (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography
                        variant="body1"
                        className="opacity60 padding-bottom--small"
                      >
                        {t("root:poNumber")}:
                      </Typography>
                      <Typography variant="body1" className="semi-bold">
                        {poNumber || t("root:na")}
                      </Typography>
                      {(authState && authState.authUser?._id) && (
                        <Typography variant="body1" className="semi-bold">
                          <Tooltip title={poNumber ? t("misc:edit") : t("misc:clickToAdd")} style={{ padding: 0 }}>
                            <IconButton
                              aria-label="toggle password visibility"
                              disabled={booking.status === BookingStatus.DRAFT ? true : false}
                              onClick={() => setIsEditablePONumber(true)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        </Typography>)}
                    </div>
                  )}
                </div>
              </div>
            </Grid>
            <Grid item xs={12} md={5} style={{ display: "flex" }}>
              <div className={styles.card} style={{ marginRight: 0 }}>
                <div>
                  <Typography
                    variant="body1"
                    className="opacity60 padding-bottom--small"
                  >
                    {booking.pickupOtherLocation && booking.pickupOtherLocation.fullAddress
                      ? `${t("root:deliveryAddress")}, ${t("root:dateTime")} :`
                      : `${t("root:pickUpLocation")}, ${t("root:dateTime")} :`}
                  </Typography>
                  <Typography variant="body1" className="semi-bold">
                    {`${booking.pickupOtherLocation && booking.pickupOtherLocation.fullAddress
                      ? booking.pickupOtherLocation.fullAddress
                      : booking.pickupServiceLocation?.name || t("root:na")
                      }`}
                  </Typography>
                  <Typography variant="body1" className="semi-bold">
                    {
                      getLocalizedDateFormat(country, booking.pickupDateTime, DATE_TYPE.EXPANDED, booking.branch?.timeZone)
                    }
                  </Typography>
                </div>
                <div>
                  <Typography
                    variant="body1"
                    className="opacity60 padding-bottom--small"
                  >
                    {booking.dropoffOtherLocation && booking.dropoffOtherLocation.fullAddress
                      ? `${t("root:collectionAddress")}, ${t("root:dateTime")}:`
                      : `${t("root:dropOffLocation")}, ${t("root:dateTime")}:`}
                  </Typography>
                  <Typography variant="body1" className="semi-bold">
                    {`${booking.dropoffOtherLocation && booking.dropoffOtherLocation.fullAddress
                      ? booking.dropoffOtherLocation.fullAddress
                      : booking.dropoffServiceLocation?.name || t("root:na")
                      }`}
                  </Typography>
                  <Typography variant="body1" className="semi-bold">
                    {booking.dropoffDateTime && getLocalizedDateFormat(country, booking.dropoffDateTime, DATE_TYPE.EXPANDED, booking.branch?.timeZone)}
                  </Typography>
                </div>
              </div>
            </Grid>
            <Grid item xs={12} style={{ marginTop: 20 }}>
              <div className={`${styles.box}`}>
                <div style={{ margin: "0 -10px -10px -10px" }}>
                  <TableContainer
                    style={{ maxHeight: "100%" }}
                    className="no-row-hover"
                  >
                    <Table stickyHeader aria-label="customized table">
                      <TableHead>
                        <StyledTableRow>
                          {COLUMN_NAMES.map((name, index) => (
                            <StyledTableCell key={index}>{name === "VAT/TAX" ? getLocalizedTaxSyntex(country) : t<string>(`misc:columnLabelToKeyMap.${name}`)}</StyledTableCell>
                          ))}
                        </StyledTableRow>
                      </TableHead>
                      <TableBody>
                        {
                          booking.isSubscription ? (
                            <>
                              <StyledTableRow>
                                <StyledTableCell
                                  rowSpan={booking?.activeSubscription?.enrollmentAmount > 0 ? 2 : 1}
                                >
                                  <Typography variant="body1">
                                    {`${isContractHire ? t("root:contractHire") : t("root:subscription")}`}
                                  </Typography>
                                </StyledTableCell>
                                <StyledTableCell>
                                  {`${weeklySubscription ? t("root:weekly") : t("root:monthly")} ${isContractHire ? t("root:contractHire") : t("root:subscription")}} ${t("root:charges")}`} <br />
                                  {booking?.activeSubscription?.subscription?.name} <br />
                                  {getTaxDescription(booking.tax || [], currency, locale)?.map(title => {
                                    return (
                                      <>
                                        <span>({title})</span><br></br>
                                      </>
                                    )
                                  })}
                                </StyledTableCell>
                                <StyledTableCell>{1}</StyledTableCell>
                                <StyledTableCell>
                                  {
                                    toCurrency(booking?.activeSubscription?.subscriptionAmount || 0, currency, locale)
                                  }
                                </StyledTableCell>
                                <StyledTableCell>{weeklySubscription ? `1 ${t("root:week")}` : `1 ${t("root:month")}`}</StyledTableCell>
                                <StyledTableCell>
                                  {t("root:na")}
                                </StyledTableCell>
                                <StyledTableCell>
                                  {toCurrency((booking.tax ? Math.round(getRetnalTaxAmount(booking.tax, booking?.activeSubscription?.subscriptionAmount || 0, bookingDurationInDays, 1)) : 0), currency, locale)}
                                </StyledTableCell>
                                <StyledTableCell>
                                  {toCurrency((booking?.activeSubscription?.subscriptionAmount || 0) + (booking.tax ? Math.round(getRetnalTaxAmount(booking.tax, booking?.activeSubscription?.subscriptionAmount || 0, bookingDurationInDays, 1)) : 0), currency, locale)}
                                </StyledTableCell>
                              </StyledTableRow>
                              {
                                booking?.activeSubscription?.enrollmentAmount > 0 && (
                                  <StyledTableRow>
                                    <StyledTableCell>
                                      {`${isContractHire ? t("root:initialRental") : t("root:enrollmentFees")}`} <br />
                                      {booking?.activeSubscription?.subscription?.name} <br />
                                    </StyledTableCell>
                                    <StyledTableCell>{1}</StyledTableCell>
                                    <StyledTableCell>
                                      {
                                        toCurrency(booking?.activeSubscription?.enrollmentAmount || 0, currency, locale)
                                      }
                                    </StyledTableCell>
                                    <StyledTableCell>{t("root:na")}</StyledTableCell>
                                    <StyledTableCell>
                                      {t("root:na")}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                      {toCurrency(Math.round(0), currency, locale)}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                      {
                                        toCurrency(booking?.activeSubscription?.enrollmentAmount || 0, currency, locale)
                                      }
                                    </StyledTableCell>
                                  </StyledTableRow>
                                )
                              }
                              {(() => {
                                if (booking.insurancePolicy?.id) {
                                  const totalVehicles = 1
                                  return (
                                    <StyledTableRow>
                                      <StyledTableCell>
                                        <Typography variant="body1">
                                          {t("root:insurance")}
                                        </Typography>
                                      </StyledTableCell>
                                      <StyledTableCell>
                                        {booking?.insurancePolicy?.name}
                                      </StyledTableCell>
                                      <StyledTableCell>{totalVehicles}</StyledTableCell>
                                      <StyledTableCell>
                                        {toCurrency(
                                          booking?.insuranceRate,
                                          currency,
                                          locale
                                        )}
                                      </StyledTableCell>
                                      <StyledTableCell>
                                        {booking.paymentDetails?.rentalTime}{" "}
                                        {getRateTypeString()}
                                      </StyledTableCell>
                                      <StyledTableCell>
                                        {t("root:na")}
                                      </StyledTableCell>
                                      <StyledTableCell>{t("root:na")}</StyledTableCell>
                                      <StyledTableCell>
                                        {toCurrency(
                                          booking?.insuranceRate *
                                          1 * (booking.paymentDetails?.rentalTime || 1),
                                          currency,
                                          locale
                                        )}
                                      </StyledTableCell>
                                    </StyledTableRow>
                                  );
                                }
                              })()}
                              {booking?.addonRequirements?.map(
                                (addonR: IAddonRequirement, idx: number) => {
                                  let taxAmount = 0;
                                  let taxDescription = "";
                                  if (addonR.hasFixedRate) {
                                    taxAmount =
                                      addonR.tax && addonR.tax.value
                                        ? Math.round((addonR.rate * addonR.tax.value / 100)) *
                                        addonR.quantity
                                        : 0;
                                  } else {
                                    taxAmount =
                                      addonR.tax && addonR.tax.value
                                        ? Math.round((addonR.rate * addonR.tax.value / 100)) *
                                        addonR.quantity *
                                        booking.paymentDetails.rentalTime
                                        : 0;
                                  }
                                  if (addonR.tax && addonR.tax.value && addonR.tax.title) {
                                    taxDescription = `(${addonR.tax.title} ${addonR.tax.value}%)`;
                                  }
                                  return (
                                    <>
                                      <StyledTableRow>
                                        {idx === 0 && (
                                          <StyledTableCell
                                            rowSpan={booking.addonRequirements.length}
                                          >
                                            <Typography variant="body1">
                                              {t("root:addons")}
                                            </Typography>
                                          </StyledTableCell>
                                        )}
                                        <StyledTableCell colSpan={1}>
                                          {addonR.addon.displayName
                                            ? addonR.addon.displayName
                                            : addonR.addon.name.toUpperCase()}{" "}
                                          <br />
                                          {taxDescription}
                                        </StyledTableCell>
                                        <StyledTableCell>{addonR.quantity}</StyledTableCell>
                                        <StyledTableCell>
                                          {toCurrency(addonR.rate, currency, locale)}
                                        </StyledTableCell>
                                        <StyledTableCell>
                                          {addonR.hasFixedRate
                                            ? t("root:na")
                                            : `${booking.paymentDetails?.rentalTime
                                            } ${getRateTypeString()}`}
                                        </StyledTableCell>
                                        <StyledTableCell>
                                          {t("root:na")}
                                        </StyledTableCell>
                                        <StyledTableCell>
                                          {toCurrency(taxAmount, currency, locale)}
                                        </StyledTableCell>
                                        <StyledTableCell>
                                          {toCurrency(
                                            getAddonRate(
                                              addonR,
                                              booking.paymentDetails?.rentalTime
                                            ) + taxAmount,
                                            currency,
                                            locale
                                          )}
                                        </StyledTableCell>
                                      </StyledTableRow>
                                    </>
                                  );
                                }
                              )}
                              {booking.discount.amount > 0 && (
                                <StyledTableRow>
                                  <StyledTableCell colSpan={6}></StyledTableCell>
                                  <StyledTableCell
                                    style={{
                                      borderLeft: "solid 1px rgba(224, 224, 224, 1)",
                                    }}
                                  >
                                    <Typography variant="h4">{t("root:discount").toUpperCase()}</Typography>
                                  </StyledTableCell>
                                  <StyledTableCell>
                                    -{" "}
                                    {toCurrency(booking.paymentDetails.discountAmount, currency, locale)}
                                  </StyledTableCell>
                                </StyledTableRow>
                              )}
                              {Boolean(booking.paymentDetails.offerDiscountAmount) && (
                                <StyledTableRow>
                                  <StyledTableCell colSpan={6}></StyledTableCell>
                                  <StyledTableCell
                                    style={{
                                      borderLeft: "solid 1px rgba(224, 224, 224, 1)",
                                    }}
                                  >
                                    <Typography variant="h4">
                                      {t("root:couponDiscount").toUpperCase()}
                                      {booking.appliedOffers?.[0]?.code ? ` (${booking.appliedOffers[0].code})` : ""}
                                    </Typography>
                                  </StyledTableCell>
                                  <StyledTableCell style={{ whiteSpace: "nowrap" }}>
                                    {`- ${toCurrency(booking.paymentDetails.offerDiscountAmount || 0, currency, locale)}`}
                                    <Typography variant="h4">
                                      {t("root:couponDiscount").toUpperCase()}
                                      {booking.appliedOffers?.[0]?.code ? ` (${booking.appliedOffers[0].code})` : ""}
                                    </Typography>
                                  </StyledTableCell>
                                  <StyledTableCell style={{ whiteSpace: "nowrap" }}>
                                    {`- ${toCurrency(booking.paymentDetails.offerDiscountAmount || 0, currency, locale)}`}
                                  </StyledTableCell>
                                </StyledTableRow>
                              )}
                              <StyledTableRow >
                                <StyledTableCell colSpan={6}></StyledTableCell>
                                <StyledTableCell
                                  style={{ borderLeft: "solid 1px rgba(224, 224, 224, 1)" }}
                                >
                                  <Typography variant="h4">
                                    {t("root:grandTotal")}
                                  </Typography>
                                </StyledTableCell>
                                <StyledTableCell >
                                  {toCurrency(
                                    booking?.paymentDetails?.totalPayableAmount,
                                    currency,
                                    locale
                                  )}
                                </StyledTableCell>
                              </StyledTableRow>
                            </>
                          ) : (
                            <>
                              {booking?.vehicleGroups?.map(
                                (vehicleGroup: IBookingVehicleGroup, idx: number) => {
                                  const vg =
                                    vehicleGroup.vehicleGroup as IVehiclePriceGroup;
                                  let rentalAmount =
                                    getApplicableRentalAmount(
                                      vehicleGroup.baseRate,
                                      vehicleGroup.priceRuleOperator,
                                      vehicleGroup.priceRuleValueType,
                                      vehicleGroup.priceRuleAmount,
                                      vehicleGroup.riskBasedPricingApplied,
                                      vehicleGroup.riskBasedPricingAmount
                                    ) *
                                    vehicleGroup.count *
                                    booking.paymentDetails.rentalTime;
                                  rentalAmount = isProrated ? rentalAmount * proratedFraction : rentalAmount
                                  const applicableTaxes = getVehicleGroupApplicableTaxesDetails(booking.tax || [], typeof vehicleGroup.vehicleGroup === 'string' ? vehicleGroup.vehicleGroup : vehicleGroup.vehicleGroup.id);
                                  const rentalTaxAmount = Math.round(getRetnalTaxAmount(
                                    applicableTaxes,
                                    rentalAmount,
                                    bookingDurationInDays,
                                    vehicleGroup.count
                                  ));
                                  const taxTitles = getTaxDescription(
                                    applicableTaxes || [],
                                    currency,
                                    locale
                                  );
                                  return (
                                    <>
                                      <StyledTableRow>
                                        {idx === 0 && (
                                          <StyledTableCell
                                            rowSpan={booking?.vehicleGroups.length}
                                          >
                                            <Typography variant="body1">
                                              {t("root:rentalPrice")}
                                            </Typography>
                                          </StyledTableCell>
                                        )}
                                        <StyledTableCell colSpan={1}>
                                          {vg.name} <br />
                                          {!website.organisation.closeGroupSharingEnabled && <>
                                            {taxTitles.length > 0 &&
                                              taxTitles.map((title) => {
                                                return (
                                                  <>
                                                    <span>({title})</span>
                                                    <br></br>
                                                  </>
                                                );
                                              })}
                                          </>
                                          }
                                        </StyledTableCell>
                                        <StyledTableCell>
                                          {vehicleGroup.count}
                                        </StyledTableCell>
                                        {!website.organisation.closeGroupSharingEnabled &&
                                          <StyledTableCell>
                                            {toCurrency(
                                              getApplicableRentalAmount(
                                                vehicleGroup.baseRate,
                                                vehicleGroup.priceRuleOperator,
                                                vehicleGroup.priceRuleValueType,
                                                vehicleGroup.priceRuleAmount,
                                                vehicleGroup.riskBasedPricingApplied,
                                                vehicleGroup.riskBasedPricingAmount
                                              ),
                                              currency,
                                              locale
                                            )}
                                          </StyledTableCell>
                                        }
                                        <StyledTableCell>
                                          {isProrated ?
                                            `${booking.paymentDetails?.rentalTime} ${getRateTypeString()} (${booking.paymentDetails.proratedBillingDays} ${t("root:daysProRated")})` :
                                            `${booking.paymentDetails?.rentalTime} ${getRateTypeString()}`}
                                        </StyledTableCell>
                                        {!website.organisation.closeGroupSharingEnabled && <>
                                          <StyledTableCell>
                                            {(() => {
                                              if (!booking?.discount?.amount || booking.discount.amount <= 0) {
                                                return t("root:na");
                                              }
                                              const originalRentalAmount = getApplicableRentalAmount(
                                                vehicleGroup.baseRate,
                                                vehicleGroup.priceRuleOperator,
                                                vehicleGroup.priceRuleValueType,
                                                vehicleGroup.priceRuleAmount,
                                                vehicleGroup.riskBasedPricingApplied,
                                                vehicleGroup.riskBasedPricingAmount
                                              ) * vehicleGroup.count * (booking.paymentDetails?.rentalTime || 1);
                                              const adjustedRentalAmount = isProrated ? originalRentalAmount * proratedFraction : originalRentalAmount;
                                              let discountAmount = 0;
                                              if (booking.discount.isPercent) {
                                                discountAmount = Math.round((adjustedRentalAmount * booking.discount.amount) / 100);
                                              } else {
                                                discountAmount = Math.min(adjustedRentalAmount, booking.discount.amount);
                                              }
                                              const discountedRentalAmount = adjustedRentalAmount - discountAmount;
                                              return toCurrency(discountedRentalAmount, currency, locale);
                                            })()}
                                          </StyledTableCell>
                                          <StyledTableCell>
                                            {(() => {
                                              let newTaxAmount = rentalTaxAmount;
                                              if (booking?.discount?.amount && booking.discount.amount > 0) {
                                                const originalRentalAmount = getApplicableRentalAmount(
                                                  vehicleGroup.baseRate,
                                                  vehicleGroup.priceRuleOperator,
                                                  vehicleGroup.priceRuleValueType,
                                                  vehicleGroup.priceRuleAmount,
                                                  vehicleGroup.riskBasedPricingApplied,
                                                  vehicleGroup.riskBasedPricingAmount
                                                ) * vehicleGroup.count * (booking.paymentDetails?.rentalTime || 1);
                                                const adjustedRentalAmount = isProrated ? originalRentalAmount * proratedFraction : originalRentalAmount;
                                                let discountAmount = 0;
                                                if (booking.discount.isPercent) {
                                                  discountAmount = Math.round((adjustedRentalAmount * booking.discount.amount) / 100);
                                                } else {
                                                  discountAmount = Math.min(adjustedRentalAmount, booking.discount.amount);
                                                }
                                                const discountedRentalAmount = adjustedRentalAmount - discountAmount;
                                                newTaxAmount = booking.tax ? getRetnalTaxAmount(applicableTaxes, discountedRentalAmount, bookingDurationInDays, vehicleGroup.count) : 0;
                                              }
                                              return toCurrency(Math.round(newTaxAmount), currency, locale);
                                            })()}
                                          </StyledTableCell>

                                          <StyledTableCell>
                                            {(() => {
                                              const originalRentalAmount = getApplicableRentalAmount(
                                                vehicleGroup.baseRate,
                                                vehicleGroup.priceRuleOperator,
                                                vehicleGroup.priceRuleValueType,
                                                vehicleGroup.priceRuleAmount,
                                                vehicleGroup.riskBasedPricingApplied,
                                                vehicleGroup.riskBasedPricingAmount
                                              ) * vehicleGroup.count * (booking.paymentDetails?.rentalTime || 1);
                                              const adjustedRentalAmount = isProrated ? originalRentalAmount * proratedFraction : originalRentalAmount;
                                              let discountAmount = 0;
                                              if (booking?.discount?.amount && booking.discount.amount > 0) {
                                                if (booking.discount.isPercent) {
                                                  discountAmount = Math.round((adjustedRentalAmount * booking.discount.amount) / 100);
                                                } else {
                                                  discountAmount = Math.min(adjustedRentalAmount, booking.discount.amount);
                                                }
                                              }
                                              const discountedRentalAmount = adjustedRentalAmount - discountAmount;
                                              let newTaxAmount = rentalTaxAmount;
                                              if (booking?.discount?.amount && booking.discount.amount > 0) {
                                                newTaxAmount = booking.tax ? getRetnalTaxAmount(applicableTaxes, discountedRentalAmount, bookingDurationInDays, vehicleGroup.count) : 0;
                                              }
                                              return toCurrency(discountedRentalAmount + Math.round(newTaxAmount), currency, locale);
                                            })()}
                                          </StyledTableCell>
                                        </>
                                        }
                                      </StyledTableRow>
                                    </>
                                  );
                                }
                              )}
                              {(() => {
                                if (booking.insurancePolicy?.id) {
                                  const totalVehicles = booking.vehicleGroups.reduce(
                                    (accumulator, current) => accumulator + current.count,
                                    0
                                  );

                                  return (
                                    <StyledTableRow>
                                      <StyledTableCell>
                                        <Typography variant="body1">
                                          {t("root:insurance")}
                                        </Typography>
                                      </StyledTableCell>
                                      <StyledTableCell>
                                        {booking?.insurancePolicy?.name}
                                        <br />
                                        {
                                          (
                                            booking?.insurancePercentageRate &&
                                            booking?.insurancePercentageRate > 0
                                          ) ?
                                          ` (${booking.insurancePercentageRate}% of rental amount.)` : ""
                                        }
                                      </StyledTableCell>
                                      <StyledTableCell>{booking?.insurancePercentageRate ? 1 : totalVehicles}</StyledTableCell>
                                      <StyledTableCell>
                                        {toCurrency(
                                          booking.insuranceRate,
                                          currency,
                                          locale
                                        )}
                                      </StyledTableCell>
                                      <StyledTableCell>
                                        {isProrated ?
                                          `${booking.paymentDetails?.rentalTime} ${getRateTypeString()} (${booking.paymentDetails.proratedBillingDays} ${t("root:daysProRated")})` :
                                          `${booking.paymentDetails?.rentalTime} ${getRateTypeString()}`}
                                      </StyledTableCell>
                                      <StyledTableCell>{t("root:na")}</StyledTableCell>
                                      <StyledTableCell>{t("root:na")}</StyledTableCell>
                                      <StyledTableCell>
                                        {toCurrency(
                                          (booking.insuranceRate *
                                            (booking?.insurancePolicy?.isPercentage ?
                                              booking.paymentDetails.rentalTime :
                                              totalVehicles * booking.paymentDetails.rentalTime
                                            )
                                          ) * proratedFraction,
                                          currency,
                                          locale
                                        )}
                                      </StyledTableCell>
                                    </StyledTableRow>
                                  );
                                }
                              })()}
                              {booking?.addonRequirements?.map(
                                (addonR: IAddonRequirement, idx: number) => {
                                  let taxAmount = 0;
                                  let taxDescription = "";
                                  if (addonR.hasFixedRate) {
                                    taxAmount =
                                      addonR.tax && addonR.tax.value
                                        ? Math.round((addonR.rate * addonR.tax.value / 100)) *
                                        addonR.quantity
                                        : 0;
                                  } else {
                                    taxAmount =
                                      addonR.tax && addonR.tax.value
                                        ? Math.round((addonR.rate * addonR.tax.value / 100)) *
                                        addonR.quantity *
                                        booking.paymentDetails.rentalTime
                                        : 0;
                                  }
                                  if (addonR.tax && addonR.tax.value && addonR.tax.title) {
                                    taxDescription = `(${addonR.tax.title} ${addonR.tax.value}%)`;
                                  }
                                  return (
                                    <>
                                      <StyledTableRow>
                                        {idx === 0 && (
                                          <StyledTableCell
                                            rowSpan={booking.addonRequirements.length}
                                          >
                                            <Typography variant="body1">
                                              {t("root:addons")}
                                            </Typography>
                                          </StyledTableCell>
                                        )}
                                        <StyledTableCell colSpan={1}>
                                          {addonR.addon.displayName
                                            ? addonR.addon.displayName
                                            : addonR.addon.name.toUpperCase()}{" "}
                                          <br />
                                          {taxDescription}
                                        </StyledTableCell>
                                        <StyledTableCell>{addonR.quantity}</StyledTableCell>
                                        <StyledTableCell>
                                          {toCurrency(addonR.rate, currency, locale)}
                                        </StyledTableCell>
                                        <StyledTableCell>
                                          {addonR.hasFixedRate ?
                                            t("root:na") :
                                            isProrated ?
                                              `${booking.paymentDetails?.rentalTime} ${getRateTypeString()} (${booking.paymentDetails.proratedBillingDays} ${t("root:daysProRated")})` :
                                              `${booking.paymentDetails?.rentalTime} ${getRateTypeString()}`
                                          }
                                        </StyledTableCell>
                                        <StyledTableCell>{t("root:na")}</StyledTableCell>
                                        <StyledTableCell>
                                          {toCurrency(
                                            addonR.hasFixedRate ? taxAmount : Math.round(taxAmount * proratedFraction),
                                            currency,
                                            locale
                                          )}
                                        </StyledTableCell>
                                        <StyledTableCell>
                                          {toCurrency(
                                            getAddonRate(addonR, booking.paymentDetails?.rentalTime) + (addonR.hasFixedRate ? taxAmount : Math.round(taxAmount * proratedFraction)),
                                            currency,
                                            locale
                                          )}
                                        </StyledTableCell>
                                      </StyledTableRow>
                                    </>
                                  );
                                }
                              )}
                              {booking?.protectionPlan && (
                                (() => {
                                  let rate = booking.protectionPlan.rate;
                                  let amount = getProtectionPlanRate(booking.protectionPlan, booking.paymentDetails?.rentalTime)
                                  const taxAmount = booking.protectionPlan.taxAmount !== undefined
                                    ? booking.protectionPlan.taxAmount
                                    : (booking.protectionPlan.hasFixedRate
                                      ? Math.round(rate * booking.protectionPlan.tax.value / 100)
                                      : Math.round(rate * booking.protectionPlan.tax.value / 100) * (booking.paymentDetails?.rentalTime || 1));
                                  const totalVehicles = booking.isSubscription ? 1 : booking.vehicleGroups.reduce(
                                    (accumulator, current) => accumulator + current.count, 0);
                                  return (
                                    <StyledTableRow>
                                      <StyledTableCell>
                                        <Typography variant="body1">{t("root:protectionPlan")}</Typography>
                                      </StyledTableCell>
                                      <StyledTableCell>{booking?.protectionPlan?.name} <br />
                                        {booking?.protectionPlan?.tax.title && `(${booking?.protectionPlan?.tax.title} ${booking?.protectionPlan?.tax.value}%)`}</StyledTableCell>
                                      <StyledTableCell>{1}</StyledTableCell>
                                      <StyledTableCell>{toCurrency(booking.protectionPlan.rate, currency, locale)}</StyledTableCell>
                                      <StyledTableCell>
                                        {booking.protectionPlan.hasFixedRate ?
                                          t("root:na") :
                                          isProrated ?
                                            `${booking.paymentDetails?.rentalTime} ${getRateTypeString()} (${booking.paymentDetails.proratedBillingDays} ${t("root:daysProRated")})` :
                                            `${booking.paymentDetails?.rentalTime} ${getRateTypeString()}`
                                        }
                                      </StyledTableCell>
                                      <StyledTableCell>{t("root:na")}</StyledTableCell>
                                      <StyledTableCell>
                                        {toCurrency(
                                          taxAmount,
                                          currency,
                                          locale
                                        )}
                                      </StyledTableCell>
                                      <StyledTableCell>
                                        {toCurrency(
                                          amount +
                                          (booking.protectionPlan.taxAmount ?? 0),
                                          currency,
                                          locale
                                        )}
                                      </StyledTableCell>
                                    </StyledTableRow>
                                  );
                                })()
                              )}
                              {booking?.locationSurcharges?.map(
                                (surcharge: ILocationSurchargeRules, idx: number) => {
                                  let taxDescription = "";
                                  const vehicleCount = booking.vehicleGroups?.reduce(
                                    (count: number, vehicleGroup) => {
                                      return count + vehicleGroup.count;
                                    },
                                    0
                                  );
                                  if (
                                    surcharge.tax &&
                                    surcharge.tax.title &&
                                    surcharge.tax.value
                                  ) {
                                    taxDescription = `(${surcharge.tax.title} ${surcharge.tax.value}%)`;
                                  }
                                  let surchargeTaxAmount = 0;
                                  let surchargeAmount = 0;
                                  if (surcharge.type === "PERCENTAGE") {
                                    surchargeAmount = Math.round(
                                      (booking.paymentDetails.rentalAmount *
                                        surcharge.value) /
                                      100
                                    );
                                    surchargeTaxAmount = Math.round(
                                      (surchargeAmount * surcharge.tax.value) / 100
                                    );
                                  } else {
                                    if (surcharge.tax && surcharge.tax.value) {
                                      if (surcharge.rate === "PER_DAY") {
                                        surchargeTaxAmount =
                                          ((surcharge.value * surcharge.tax.value) / 100) *
                                          bookingDurationInDays *
                                          vehicleCount;
                                      } else if (surcharge.rate === "ONE_TIME") {
                                        surchargeTaxAmount =
                                          ((surcharge.value * surcharge.tax.value) / 100) *
                                          vehicleCount;
                                      }
                                    }
                                    if (surcharge.rate === "PER_DAY") {
                                      surchargeAmount =
                                        surcharge.value *
                                        bookingDurationInDays *
                                        vehicleCount;
                                    } else if (surcharge.rate === "ONE_TIME") {
                                      surchargeAmount = surcharge.value * vehicleCount;
                                    }
                                  }
                                  return (
                                    <>
                                      {!website.organisation.closeGroupSharingEnabled && (
                                        <StyledTableRow>
                                          {idx === 0 && (
                                            <StyledTableCell
                                              rowSpan={booking.locationSurcharges.length}
                                            >
                                              <Typography variant="body1">
                                                {t("root:locationSurcharges")}
                                              </Typography>
                                            </StyledTableCell>
                                          )}
                                          <StyledTableCell colSpan={1}>
                                            {surcharge.name} <br />
                                            {surcharge.type === "PERCENTAGE" && (
                                              <span>
                                                {" "}
                                                (
                                                {`${surcharge.value}% ${t("root:ofTotalRentalAmount")}`}
                                                ){" "}
                                              </span>
                                            )}{" "}
                                            <br />
                                            {taxDescription && (
                                              <span>{taxDescription}</span>
                                            )}
                                          </StyledTableCell>
                                          <StyledTableCell>{surcharge.type !== "PERCENTAGE" ? vehicleCount : t("root:na")}</StyledTableCell>
                                          <StyledTableCell>
                                            {surcharge.type === "PERCENTAGE"
                                              ? toCurrency(surcharge.payableAmount || 0, currency, locale)
                                              : toCurrency(surcharge.value, currency, locale)}
                                          </StyledTableCell>
                                          <StyledTableCell>
                                            {surcharge.type === "PERCENTAGE" ? t("root:na") : surcharge.rate === "PER_DAY" ? `${booking.paymentDetails?.rentalTime} ${getRateTypeString()}` : t("root:na")}
                                          </StyledTableCell>
                                          <StyledTableCell>
                                            {t("root:na")}
                                          </StyledTableCell>
                                          <StyledTableCell>
                                            {surcharge.tax && surcharge.tax.value ?
                                              toCurrency(surcharge.payableTaxAmount || 0, currency, locale) :
                                              toCurrency(0, currency, locale)}
                                          </StyledTableCell>
                                          <StyledTableCell>
                                            {surcharge.tax && surcharge.tax.value ?
                                              toCurrency(
                                                ((surcharge.payableAmount || 0) + (surcharge.payableTaxAmount || 0)),
                                                currency,
                                                locale
                                              ) : toCurrency(
                                                surcharge.payableAmount || 0,
                                                currency,
                                                locale
                                              )}
                                          </StyledTableCell>
                                        </StyledTableRow>
                                      )}

                                    </>
                                  );
                                }
                              )}
                              {(() => {
                                if (!website.organisation.closeGroupSharingEnabled && booking.oneWayRentalFee && booking.oneWayRentalFee.amount) {
                                  const totalVehicles = booking.vehicleGroups.reduce(
                                    (accumulator, current) => accumulator + current.count,
                                    0
                                  );
                                  const oneWayRentalTax = booking.oneWayRentalFee.taxable ? Math.round((booking.oneWayRentalFee.amount * totalVehicles * booking.oneWayRentalFee.tax.value) / 100) : 0;
                                  return (
                                    <StyledTableRow>
                                      <StyledTableCell>
                                        <Typography variant="body1">
                                          {t("root:oneWayFee")}
                                        </Typography>
                                      </StyledTableCell>
                                      <StyledTableCell>
                                        {t("root:oneWayRentalCharge")}
                                      </StyledTableCell>
                                      <StyledTableCell>{totalVehicles}</StyledTableCell>
                                      <StyledTableCell>
                                        {toCurrency(
                                          booking?.oneWayRentalFee.amount,
                                          currency,
                                          locale
                                        )}
                                      </StyledTableCell>
                                      <StyledTableCell>
                                        {t("root:na")}
                                      </StyledTableCell>
                                      <StyledTableCell>
                                        {t("root:na")}
                                      </StyledTableCell>
                                      <StyledTableCell>{toCurrency(oneWayRentalTax, currency, locale)}</StyledTableCell>
                                      <StyledTableCell>
                                        {toCurrency(
                                          (booking?.oneWayRentalFee.amount * totalVehicles) + oneWayRentalTax,
                                          currency,
                                          locale
                                        )}
                                      </StyledTableCell>
                                    </StyledTableRow>
                                  );
                                }
                              })()}
                              {booking.discount.amount > 0 && (
                                <StyledTableRow>
                                  <StyledTableCell
                                    colSpan={6}
                                  >
                                  </StyledTableCell>
                                  <StyledTableCell
                                    style={{
                                      borderLeft: "solid 1px rgba(224, 224, 224, 1)",
                                    }}
                                  >
                                    <Typography variant="h4">{t("root:discount").toUpperCase()}</Typography>
                                  </StyledTableCell>
                                  <StyledTableCell>
                                    -{" "}
                                    {toCurrency(booking.paymentDetails.discountAmount, currency, locale)}
                                  </StyledTableCell>
                                </StyledTableRow>
                              )}
                              {Boolean(booking.paymentDetails.offerDiscountAmount) && (
                                <StyledTableRow>
                                  <StyledTableCell
                                    colSpan={6}
                                  >
                                  </StyledTableCell>
                                  <StyledTableCell
                                    style={{
                                      borderLeft: "solid 1px rgba(224, 224, 224, 1)",
                                    }}
                                  >
                                    <Typography variant="h4">
                                      {t("root:couponDiscount").toUpperCase()}
                                      {booking.appliedOffers?.[0]?.code ? ` (${booking.appliedOffers[0].code})` : ""}
                                    </Typography>
                                  </StyledTableCell>
                                  <StyledTableCell style={{ whiteSpace: "nowrap" }}>
                                    {`- ${toCurrency(booking.paymentDetails.offerDiscountAmount || 0, currency, locale)}`}
                                    <Typography variant="h4">
                                      {t("root:couponDiscount").toUpperCase()}
                                      {booking.appliedOffers?.[0]?.code ? ` (${booking.appliedOffers[0].code})` : ""}
                                    </Typography>
                                  </StyledTableCell>
                                  <StyledTableCell style={{ whiteSpace: "nowrap" }}>
                                    {`- ${toCurrency(booking.paymentDetails.offerDiscountAmount || 0, currency, locale)}`}
                                  </StyledTableCell>
                                </StyledTableRow>
                              )}
                              <StyledTableRow>
                                <StyledTableCell
                                  colSpan={6}
                                  rowSpan={3 + (booking.discount.amount > 0 ? 1 : 0) + (booking.paymentDetails.offerDiscountAmount ? 1 : 0)}
                                >
                                  <>
                                    {(booking.rateTypeName === "weekly" ||
                                      booking.rateTypeName === "monthly") && booking.billingFrequency && (
                                        <Table stickyHeader className="no-row-hover">
                                          <TableHead style={{ paddingTop: "10px" }}>
                                            <StyledTableRow>
                                              {!website.organisation.closeGroupSharingEnabled &&
                                                <Typography variant="h4">
                                                  {t("root:nextBilling")} ({booking.billingFrequency} {rateTypeName(booking.billingFrequency, booking.rateTypeName)})
                                                </Typography>
                                              }
                                            </StyledTableRow>
                                          </TableHead>
                                          <TableBody>
                                            {!website.organisation.closeGroupSharingEnabled && <>
                                              <StyledTableRow>
                                                <StyledTableCell>

                                                  <Typography variant="body1">
                                                    {t("root:subTotal")}
                                                  </Typography>
                                                </StyledTableCell>
                                                <StyledTableCell
                                                  style={{
                                                    borderLeft:
                                                      "solid 1px rgba(224, 224, 224, 1)",
                                                  }}
                                                >
                                                  {toCurrency(
                                                    (weeklyRentalAmount +
                                                      weeklyAddonAmount +
                                                      weeklyProtectionPlanAmount +
                                                      weeklyInsuranceAmount),
                                                    currency,
                                                    locale
                                                  )}
                                                </StyledTableCell>
                                              </StyledTableRow>
                                            </>
                                            }
                                            {booking.discount.amount > 0 && (
                                              <StyledTableRow>
                                                <StyledTableCell>
                                                  <Typography variant="body1">
                                                    {t("root:discount")}
                                                  </Typography>
                                                </StyledTableCell>
                                                <StyledTableCell
                                                  style={{
                                                    borderLeft:
                                                      "solid 1px rgba(224, 224, 224, 1)",
                                                  }}
                                                >
                                                  -{" "}
                                                  {toCurrency(
                                                    booking.discount.amount /
                                                    (booking.paymentDetails?.rentalTime || 1),
                                                    currency,
                                                    locale
                                                  )}
                                                </StyledTableCell>
                                              </StyledTableRow>
                                            )}
                                            {!website.organisation.closeGroupSharingEnabled && <>
                                              <StyledTableRow>
                                                <StyledTableCell>
                                                  <Typography variant="body1">
                                                    {t("root:vatTax")}
                                                  </Typography>
                                                </StyledTableCell>
                                                <StyledTableCell
                                                  style={{
                                                    borderLeft:
                                                      "solid 1px rgba(224, 224, 224, 1)",
                                                  }}
                                                >
                                                  {toCurrency(
                                                    weeklyTaxAmount,
                                                    currency,
                                                    locale
                                                  )}
                                                </StyledTableCell>
                                              </StyledTableRow>
                                              <StyledTableRow>
                                                <StyledTableCell>
                                                  <Typography variant="body1">
                                                    {t("root:total")}
                                                  </Typography>
                                                </StyledTableCell>
                                                <StyledTableCell
                                                  style={{
                                                    borderLeft:
                                                      "solid 1px rgba(224, 224, 224, 1)",
                                                  }}
                                                >
                                                  {toCurrency(
                                                    (weeklyRentalAmount +
                                                      weeklyAddonAmount +
                                                      weeklyProtectionPlanAmount +
                                                      weeklyInsuranceAmount +
                                                      weeklyTaxAmount),
                                                    currency,
                                                    locale
                                                  )}
                                                </StyledTableCell>
                                              </StyledTableRow>
                                            </>
                                            }
                                          </TableBody>
                                        </Table>
                                      )}
                                    {
                                      booking.taxExempted ?
                                        <Typography variant="h4">
                                          {`* ${t("root:thisIsA")} ${getLocalizedTaxSyntex(country)} ${t("root:exemptedBooking")}.`}
                                        </Typography> : <Typography></Typography>
                                    }
                                    {booking.zeroCostBooking ?
                                      <Typography variant="h4">
                                        *{t("root:thisIstestCourtsey")}{getLocalizedBookingSyntex(country).toLowerCase()}.
                                      </Typography> : <Typography></Typography>
                                    }
                                  </>
                                </StyledTableCell>
                                {!website.organisation.closeGroupSharingEnabled && <>
                                  <StyledTableCell
                                    style={{ borderLeft: "solid 1px rgba(224, 224, 224, 1)" }}
                                  >
                                    <Typography variant="h4">
                                      {`${booking.rateTypeName === RATE_TYPES.WEEKLY && booking.billingFrequency == 1
                                        ? t("root:firstWeekTotal")
                                        : booking.rateTypeName === RATE_TYPES.MONTHLY && booking.billingFrequency == 1
                                          ? t("root:firstMonthTotal")
                                          : t("root:grandTotal")
                                        }`}
                                    </Typography>
                                  </StyledTableCell>
                                  <StyledTableCell>
                                    {toCurrency(
                                      booking?.paymentDetails?.totalPayableAmount,
                                      currency,
                                      locale
                                    )}
                                  </StyledTableCell>
                                </>
                                }
                              </StyledTableRow>
                            </>
                          )

                        }

                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>
              </div>
            </Grid>
            {!!website.organisation.discountModuleEnabled && booking.status !== BookingStatus.IN_PROGRESS && (
              <Grid item xs={12} style={{ marginTop: 20 }}>
                <div className={styles.box}>
                  <DiscountOffersSection
                    booking={booking}
                    onBookingUpdated={(updated) => {
                      setBooking({ ...booking, ...updated });
                      props.onBookingUpdated?.(updated);
                    }}
                  />
                </div>
              </Grid>
            )}
            <Grid item xs={12}>
              <div className={styles.box}>
                {!website.organisation.closeGroupSharingEnabled &&
                  <Typography variant="body1">
                    {`*${getLocalizedTaxSyntex(country)} ${t("root:isCalculatedAgainst")} ${booking.isSubscription ? t("root:subscription") : t("root:rental")} ${t("root:priceAndSelectedAddons")}`}
                  </Typography>
                }
                {(booking.rateTypeName === "weekly" ||
                  booking.rateTypeName === "monthly") && !website.organisation.closeGroupSharingEnabled && (
                    <Typography
                      variant="body1"
                      className="semi-bold"
                      style={{ paddingTop: 5 }}
                    >
                      *{t("root:recurringWeeklyMonthlyInvoice")}
                    </Typography>
                  )}
                {quoteInfo && branch && branch.quoteInfo && (
                  <Typography
                    variant="body1"
                    className="semi-bold"
                    style={{ paddingTop: 5 }}
                  >
                    *{branch.quoteInfo}.
                  </Typography>
                )}
                {booking.zeroCostBookingNote && (
                  <>
                    <Typography variant="h4" style={{ marginTop: 5 }}>
                      {t("root:additionalNote")}:
                    </Typography>
                    <Typography variant="body1">
                      {booking.zeroCostBookingNote}
                    </Typography>
                  </>
                )}
                {!website.organisation.closeGroupSharingEnabled &&
                  <Typography
                    variant="h3"
                    // className="semi-bold"
                    style={{ paddingTop: 5, fontWeight: "bolder" }}
                  >
                    {collectDeposit ? `${t("root:deposit")}:` : `${t("root:deposit")} (${t("root:payableAtVehicleCollection")}): `}
                    <span className="margin-left">
                      {toCurrency(
                        booking.depositDetails?.amount || 0,
                        currency,
                        locale
                      )}
                    </span>
                  </Typography>
                }
              </div>
            </Grid>
          </Grid>
        )
      }
    </>
  );
};

export default Quote;